/**
 * Similar Transactions Hook
 * Fetches and manages similar uncategorized transactions for bulk categorization
 * Based on WP-CAT-2: Apply to Similar Transactions Feature
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  similarTransactionsService,
  type SimilarTransactionItem,
  type SimilarTransactionsResponse,
  type BulkUpdateWithRuleRequest,
  type BulkUpdateWithRuleResponse,
} from '@/services/similarTransactions';

// ============================================================================
// Query Keys
// ============================================================================

export const similarTransactionsKeys = {
  all: ['similar-transactions'] as const,
  byId: (transactionId: string) => [...similarTransactionsKeys.all, transactionId] as const,
};

// ============================================================================
// Types
// ============================================================================

export interface UseSimilarTransactionsOptions {
  transactionId: string;
  enabled?: boolean;
}

export interface UseSimilarTransactionsResult {
  originalTransaction: SimilarTransactionItem | null;
  similarTransactions: SimilarTransactionItem[];
  /** null when there is nothing to suggest (e.g. a person-to-person payment, WP-SEC-20) */
  suggestedPattern: string | null;
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching similar uncategorized transactions
 *
 * @param options - Configuration options
 * @returns Similar transactions data and query state
 *
 * @example
 * const { similarTransactions, suggestedPattern, isLoading } = useSimilarTransactions({
 *   transactionId: 'abc-123',
 *   enabled: true,
 * });
 */
export function useSimilarTransactions(
  options: UseSimilarTransactionsOptions
): UseSimilarTransactionsResult {
  const { transactionId, enabled = true } = options;

  const query = useQuery<SimilarTransactionsResponse, Error>({
    queryKey: similarTransactionsKeys.byId(transactionId),
    queryFn: () => similarTransactionsService.getSimilarTransactions(transactionId),
    enabled: enabled && !!transactionId,
    // Don't refetch too aggressively - similar transactions are computed on-demand
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  });

  return {
    originalTransaction: query.data?.originalTransaction ?? null,
    similarTransactions: query.data?.similarTransactions ?? [],
    suggestedPattern: query.data?.suggestedPattern ?? null,
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for bulk updating transactions with optional rule creation
 *
 * @returns Mutation for bulk update with rule
 *
 * @example
 * const { mutateAsync: bulkUpdateWithRule } = useBulkUpdateWithRule();
 *
 * await bulkUpdateWithRule({
 *   transactionIds: ['id1', 'id2'],
 *   categoryId: 'cat-123',
 *   createRule: true,
 *   rulePattern: 'STARBUCKS',
 * });
 */
export function useBulkUpdateWithRule() {
  const queryClient = useQueryClient();

  return useMutation<BulkUpdateWithRuleResponse, Error, BulkUpdateWithRuleRequest>({
    mutationFn: (request) => similarTransactionsService.bulkUpdateWithRule(request),
    // onSettled: a partial failure (BulkUpdatePartialError) still saved some updates.
    onSettled: () => {
      // Invalidate transactions list to refetch with updated categories
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      // Invalidate spending data since categories changed
      queryClient.invalidateQueries({ queryKey: ['spending-by-category'] });
      // Invalidate similar transactions cache
      queryClient.invalidateQueries({ queryKey: similarTransactionsKeys.all });
      // Invalidate categorization rules if a new rule was created
      queryClient.invalidateQueries({ queryKey: ['categorization-rules'] });
    },
  });
}

export default useSimilarTransactions;
